import { ActivitySpinnerWidget } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { CommonStyles } from '~common/react/CommonStyles';
import { ExplorerStyles } from '~src/app/spaces/Explorer/ExplorerStyles';
import { ExtractTasks } from '~src/extract/resources/ExtractTasks';
import { cssClass, fillStyle, flexColumnStyle } from '~src/style';
import { ServerFilterClauses } from '~src/ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { IExtractTaskInfo } from '~src/ts/models/IExtractTaskInfo';
import { IPublishedConnectionDetailInfo } from '~src/ts/models/IPublishedConnectionInfo';
import {
  IPublishedConnectionTaskInfo,
  PublishedConnectionExtractRefreshLevel
} from '~src/ts/models/IPublishedConnectionTaskInfo';

import { PublishedConnectionScheduledTasksGrid } from './PublishedConnectionScheduledTasksGrid';
import { PublishedConnectionTasksActionBar } from './PublishedConnectionTasksActionBar';

const cssPrefix = 'PublishedConnectionScheduledTasksView';
const rootClass = cssClass(cssPrefix, 'root', fillStyle, flexColumnStyle, {
  backgroundColor: CommonStyles.Colors.Grey245,
  overflow: 'auto'
});

const actionBarClass = cssClass(cssPrefix, 'actionBar', {
  borderBottom: `1px solid ${CommonStyles.Colors.Grey203}`,
});

function renderSpinner(): JSX.Element {
  return (
    <div className={ExplorerStyles.ListViewWaitingDiv}>
      <div style={{ paddingTop: 48 }}>
        <ActivitySpinnerWidget testId={'published-connection-task-view-spinner'}/>
      </div>
    </div>
  );
}

export type PublishedConnectionTasksMap = { readonly [id: string]: IPublishedConnectionTaskInfo };

export type PublishedConnectionScheduledTasksViewProps = Readonly<{
  editActionsAllowed: boolean;
  isServerOrSiteAdmin: boolean;
  publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  updateScheduledTasksCount: () => void;
}>;

export function PublishedConnectionScheduledTasksView(props: PublishedConnectionScheduledTasksViewProps): JSX.Element {
  const { editActionsAllowed, isServerOrSiteAdmin, publishedConnectionDetailInfo, updateScheduledTasksCount } = props;

  const [busyFetchingItems, setBusyFetchingItems] = React.useState(false);
  const [tasks, setTasks] = React.useState<ReadonlyArray<IPublishedConnectionTaskInfo>>([]);
  const [selectedTasks, setSelectedTasks] = React.useState<PublishedConnectionTasksMap>({});

  const handleSelectAllTasks = React.useCallback(() => {
      const selected: { [taskId: string]: IPublishedConnectionTaskInfo } = {};
      tasks.forEach(task => selected[task.id] = task);
      setSelectedTasks(selected);
    },
    [tasks]
  );

  const handleClearTasks = React.useCallback(() => setSelectedTasks({}), []);

  const handleDeselectTask = React.useCallback((task: IPublishedConnectionTaskInfo) => {
      const { [task.id]: removed, ...rest } = selectedTasks;
      setSelectedTasks(rest);
    },
    [selectedTasks, setSelectedTasks]
  );

  const handleSelectTask = React.useCallback(
    (task: IPublishedConnectionTaskInfo) => setSelectedTasks({
      ...selectedTasks,
      [task.id]: task
    }),
    [selectedTasks, setSelectedTasks]
  );

  const selectedTaskList = React.useMemo((): ReadonlyArray<IPublishedConnectionTaskInfo> => {
      return Object.keys(selectedTasks).map(key => selectedTasks[key]);
    },
    [selectedTasks]
  );

  const updateSelection = React.useCallback((updatedTaskList: ReadonlyArray<IPublishedConnectionTaskInfo>) => {
      const updatedTaskSelection: { [taskId: string]: IPublishedConnectionTaskInfo } = {};
      const taskMap = _.indexBy(updatedTaskList, 'id');

      // if selected task still exists, retain the selection
      selectedTaskList.forEach(task => {
        if (taskMap[task.id]) {
          updatedTaskSelection[task.id] = task;
        }
        setSelectedTasks(updatedTaskSelection);
      });
    },
    [selectedTaskList]
  );

  const fetchTasks = React.useCallback(
    () => {
      setBusyFetchingItems(true);
      updateScheduledTasksCount();
      const tasksFetcher = ExtractTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.publishedConnection(publishedConnectionDetailInfo.id));

      SliceableHelper.fetchAll(tasksFetcher).then(taskListResult => {
        const tables = _.indexBy(publishedConnectionDetailInfo.tables, 'luid');
        const scheduledTasks = taskListResult.items.map((item: IExtractTaskInfo): IPublishedConnectionTaskInfo => ({
          id: item.id,
          publishedConnection: publishedConnectionDetailInfo,
          name: item.name,
          schedule: item.schedule,
          contentType: 'extractTask',
          extractRefreshLevel: item.extractServiceTask.transactionLevel === PublishedConnectionExtractRefreshLevel.Database ?
            PublishedConnectionExtractRefreshLevel.Database : PublishedConnectionExtractRefreshLevel.Table,
          // filter tables which might be deleted
          tables: _.compact(item.extractServiceTask.startingItemIds?.map(tableLuid => tables[tableLuid])) || []
        }));

        setTasks(scheduledTasks);
        updateSelection(scheduledTasks);
        setBusyFetchingItems(false);
      });
    },
    [publishedConnectionDetailInfo, updateScheduledTasksCount, updateSelection]
  );

  const [currentPublishedConnectionId, setCurrentPublishedConnectionId] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
      // fetch tasks again only if switched to different pconn
      if (currentPublishedConnectionId !== publishedConnectionDetailInfo.id) {
        setCurrentPublishedConnectionId(publishedConnectionDetailInfo.id);
        fetchTasks();
      }
    },
    [currentPublishedConnectionId, fetchTasks, publishedConnectionDetailInfo.id]
  );

  if (busyFetchingItems) {
    return renderSpinner();
  } else {
    return (
      <div className={rootClass}>
        {editActionsAllowed ?
          <div className={actionBarClass}>
            <PublishedConnectionTasksActionBar
              clearTasks={handleClearTasks}
              publishedConnectionInfo={publishedConnectionDetailInfo}
              selectAllTasks={handleSelectAllTasks}
              selectedTasks={selectedTaskList}
              onUpdateTasks={fetchTasks}
            />
          </div> : null
        }
        <PublishedConnectionScheduledTasksGrid
          editActionsAllowed={editActionsAllowed}
          isServerOrSiteAdmin={isServerOrSiteAdmin}
          onDeselectTask={handleDeselectTask}
          onSelectTask={handleSelectTask}
          onUpdateTasks={fetchTasks}
          publishedConnectionInfo={publishedConnectionDetailInfo}
          selectedTasks={selectedTasks}
          tasks={tasks}
        />
      </div>
    );
  }
}
